import React, { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { displayToastNotification } from "@/utils/helperFunctions/displayToastNotification";
import { actionType, eventName } from "../../../../../constants/events/eventName";
import { BOOKING_CLASS, BOOKING_IS_ETIHAD_BOOKING, NOTIFICATION_MESSAGE_TYPE, STATUS, TYPE_OF_FLOW_IN_GROUP } from "../../../../../constants/variables";
import { BookingDetailContext } from "../../../../../context/BookingDetailsContext";
import { extractResource } from "../../../../../helper/extractResource";
import { getToken } from "../../../../../helper/token";
import {
  checkAndGetCurrentBookingInState,
  checkAndGetCurrentBookingInStateWithParams,
  saveBookingInRedux
} from "../../../../../services/manageBooking";
import { displayNotificationMessage } from "../../../../../services/notificationMessagePopup";
import { trackUserActivity } from "../../../../../services/trackUserActivity";
import { initiateBooking, resetVoucherList, setShowPriceBreakDown } from "../../../../../store/slices/booking";
import { lastNameOrEmailValidator, pnrValidator } from "../../../../../utils/helperFunctions/userDataValidators";
import { ButtonV2 } from "../../../atoms/ButtonV2/ButtonV2";
import { TextFieldV2 } from "../../../atoms/TextFieldV2/TextFieldV2";
import { useCommonContext } from "context/CommonDataContext";
import { handleGroupBooking } from "@/services/groupBooking/groupBooking";
import storage from "constants/storage";
import CheckInEtihadBookings from "../../CheckIn/CheckInEtihadBookingV2/checkInEtihadBookingRetrieve";

export interface RetrieveBookingWithPNRFormProps {
  resourceData: {};
  pnrFieldPlaceholder: string;
  emailFieldPlaceholder: string;
  submitCTAText: string;
  setGClassOpenModal: (flag: boolean) => void;
}

type FormData = {
  pnrDetails: string;
  lastName: string;
};

type ErrorTexts = {
  pnrError: string;
  lastNameEmailError: string;
};

export function RetrieveBookingWithPNRFormV2({
  resourceData,
  pnrFieldPlaceholder,
  emailFieldPlaceholder,
  submitCTAText,
  setGClassOpenModal,
}: RetrieveBookingWithPNRFormProps) {
  const [errorTexts, setErrorTexts] = useState<ErrorTexts>({
    pnrError: "",
    lastNameEmailError: ""
  });
  const [formData, setFormData] = useState<FormData>({ pnrDetails: "", lastName: "" });
  const router = useRouter();
  const dispatch = useDispatch();
  const [timerId, setTimerId] = useState<any>(null);
  const { setCurrentBookingInStateData } = useContext(BookingDetailContext);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const { setSelectedCurrency } = useCommonContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  let currentBookingState: any;

  useEffect(() => {
    dispatch(resetVoucherList());
  }, [])

  const checkValidations = (event: any) => {
    const { name, value } = event.target;

    if (name === "pnrDetails" && value) {
      setErrorTexts({ ...errorTexts, pnrError: pnrValidator(value) });
    } else if (name === "lastName" && value) {
      setErrorTexts({ ...errorTexts, lastNameEmailError: lastNameOrEmailValidator(value) });
    }
  };

  const debounce = (event: any, delay: any) => {
    const { name } = event.target;

    if (name === "pnrDetails") {
      setErrorTexts({ ...errorTexts, pnrError: "" });
    }
    if (name === "lastName") {
      setErrorTexts({ ...errorTexts, lastNameEmailError: "" });
    }

    if (timerId) {
      clearTimeout(timerId);
      setTimerId(null);
    }
    setTimerId(
      setTimeout(() => {
        checkValidations(event);
      }, delay)
    );
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleChange = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;

    setFormData((prevValues) => ({ ...prevValues, [name]: value }));
    debounce(event, 1000);
  };
  const toastNotification = (toasterType: string) => {
    displayToastNotification(NOTIFICATION_MESSAGE_TYPE.ERROR, toasterType);
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    storage.removeItem("persist:root"); //initiate Booking Redux;
    dispatch(initiateBooking());
    setIsLoader(true);

    let isValidPnr: boolean = true;
    if (formData?.pnrDetails.length < 6 || formData?.pnrDetails.length > 6) {
      isValidPnr = false;
    }

    if (isValidPnr) {
      const LastName = formData.lastName.includes("@") ? null : formData.lastName.trim();
      const Email = formData.lastName.includes("@") ? formData.lastName.trim() : null;
      const params = { pnrDetails: formData.pnrDetails.trim(), lastName: LastName, email: Email };
      

      const paramGroupBooking = {
        ...params,
        bookingType: TYPE_OF_FLOW_IN_GROUP.MY_BOOKING,
        dispatch
      }

      const groupBookingHandled = await handleGroupBooking(paramGroupBooking, true);

      if(groupBookingHandled === BOOKING_IS_ETIHAD_BOOKING){
        setIsModalOpen(true);
        setIsLoader(false);
        return;
      }

      if (groupBookingHandled && groupBookingHandled !== BOOKING_CLASS.NORMAL_BOOKING) {
        setGClassOpenModal(true); 
        setIsLoader(false);
        return;
      }

      if (groupBookingHandled === false) {
        setIsLoader(false);
        return;
      }

      //From here Normal booking is continue and for group booking it will return.
      let response = await checkAndGetCurrentBookingInStateWithParams(params);

      const isCancelled = response?.info?.status === STATUS.CLOSED && response?.journeys?.length === 0 ? true : false;
      if (isCancelled && response?.isGroupBooking === true) {
        displayNotificationMessage(NOTIFICATION_MESSAGE_TYPE.ERROR, extractResource(resourceData, "bookingCancelled"));
        setIsLoader(false);
        return;
      }

      if (response && response?.restriction?.allowCheckIn === false && response?.isGroupBooking === true) {
        toastNotification(extractResource(resourceData, "noBookingFound"));
        setIsLoader(false);
        return;
      }
      dispatch(setShowPriceBreakDown({ showPriceBreakDown: response?.breakdown?.showPriceBreakdown }));
      if (response?.breakdown) {
        currentBookingState = await checkAndGetCurrentBookingInState();
        trackUserActivity({
          actionType: actionType.CLICK,
          eventName: eventName.RETRIVE_BOOKING_WITH_PNR,
          details: { userInput: params, currentBookingInState: currentBookingState?.data?.data }
        });
        setSelectedCurrency(currentBookingState?.data?.data?.currencyCode);
        saveBookingInRedux({ currentBooking: currentBookingState?.data?.data, refreshBookingData: true, dispatch });
        setCurrentBookingInStateData(currentBookingState);
      }
      if (currentBookingState?.data?.data?.journeys.length >= 0) {
        router.push("/manage-booking/retrieve-booking");
      } else {
        setIsLoader(false);

        displayNotificationMessage("error", extractResource(resourceData, "noBookingFound"));
      }
      return;
    }
    displayNotificationMessage("error", extractResource(resourceData, "noBookingFound"));
    setErrorTexts({ ...errorTexts, pnrError: "Please enter a valid PNR" });
    setIsLoader(false);
  };

  return (
    <form
      method="POST"
      onSubmit={handleSubmit}
      className="w-full flex flex-col justify-between space-y-4 md:space-x-3 md:space-y-0 mt-6 md:flex-row pb-4"
    >
      <div className="w-full">
        <TextFieldV2
          label={pnrFieldPlaceholder}
          type="text"
          name="pnrDetails"
          value={formData.pnrDetails}
          additionalInputClassNames="uppercase"
          onChange={handleChange}
          errorText={errorTexts.pnrError}
        />
      </div>
      <div className="w-full">
        <TextFieldV2
          label={emailFieldPlaceholder}
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          errorText={errorTexts.lastNameEmailError}
        />
      </div>
      <div>
        <ButtonV2
          type="submit"
          color="primary"
          blok={{ text: submitCTAText, variant: "primary", isFullWidthOnMobile: true }}
          isLoading={isLoader}
          isDisabled={
            isLoader ||
            !formData.lastName ||
            !formData.pnrDetails ||
            errorTexts.pnrError.length !== 0 ||
            errorTexts.lastNameEmailError.length !== 0
          }
        />
      </div>
      <CheckInEtihadBookings isOpen={isModalOpen} onClose={handleModalClose} flowType={TYPE_OF_FLOW_IN_GROUP.MY_BOOKING} />
    </form>
  );
}
